/**
 * user-ht-gold theme registry
 * Template keys: user-ht-gold/{themeId}
 */

import {
  DEFAULT_HT_GOLD_THEME_ID,
  HT_GOLD_THEMES,
  type HtGoldThemeTokens,
} from '../lib/htGoldTheme'

const THEME_STYLES = [
  '/css/style.css',
  '/css/bootstrap.min.css',
  '/css/animate.min.css',
  '/css/fontawesome-all.min.css',
]

/** CDN folder for lottery flags / shared HT layout chrome (pages fallback). */
const CDN_LAYOUT_FOLDER = 'HuayToday-Blue'

export interface ThemeMetadata {
  id: string
  name: string
  displayName: string
  description: string
  folder: string
  colorScheme: {
    primary: string
    secondary: string
    primaryLight: string
    dark: string
  }
  assets: {
    styles: string[]
    scripts: string[]
    favicon: string
    logo: string
  }
  status: 'active' | 'beta' | 'deprecated'
}

const LABELS: Record<string, { displayName: string; description: string }> = {
  'gold-navy': { displayName: 'Gold Navy', description: 'ทอง–น้ำเงินมาตรฐาน' },
  'amber-royal': { displayName: 'Amber Royal', description: 'ทองเข้ม พรีเมียม' },
  'copper-navy': { displayName: 'Copper Navy', description: 'ส้มทองอุ่น' },
  'emerald-gold': { displayName: 'Emerald Gold', description: 'เขียวมรกต + ทอง' },
  'ruby-gold': { displayName: 'Ruby Gold', description: 'แดงไวน์ + ทอง' },
  'violet-gold': { displayName: 'Violet Gold', description: 'ม่วงราชา + ทอง' },
  'ocean-pearl': { displayName: 'Ocean Pearl', description: 'ฟ้าทะเล + ครีมทอง' },
  'midnight-cyan': { displayName: 'Midnight Cyan', description: 'น้ำเงินดำ + ฟ้าสด' },
  'slate-champagne': { displayName: 'Slate Champagne', description: 'เทาเข้ม + แชมเปญ' },
  'forest-honey': { displayName: 'Forest Honey', description: 'เขียวป่า + น้ำผึ้ง' },
  'gold-navy-white': { displayName: 'Gold Navy White', description: 'โทนเดิมผสมขาว' },
  'amber-royal-white': { displayName: 'Amber Royal White', description: 'โทนเดิมผสมขาว' },
  'copper-navy-white': { displayName: 'Copper Navy White', description: 'โทนเดิมผสมขาว' },
  'emerald-gold-white': { displayName: 'Emerald Gold White', description: 'โทนเดิมผสมขาว' },
  'ruby-gold-white': { displayName: 'Ruby Gold White', description: 'โทนเดิมผสมขาว' },
  'violet-gold-white': { displayName: 'Violet Gold White', description: 'โทนเดิมผสมขาว' },
  'ocean-pearl-white': { displayName: 'Ocean Pearl White', description: 'โทนเดิมผสมขาว' },
  'midnight-cyan-white': { displayName: 'Midnight Cyan White', description: 'โทนเดิมผสมขาว' },
  'slate-champagne-white': { displayName: 'Slate Champagne White', description: 'โทนเดิมผสมขาว' },
  'forest-honey-white': { displayName: 'Forest Honey White', description: 'โทนเดิมผสมขาว' },
}

function buildTheme(id: string, tokens: HtGoldThemeTokens): ThemeMetadata {
  const label = LABELS[id] || { displayName: id, description: id }
  return {
    id,
    name: id,
    displayName: label.displayName,
    description: label.description,
    folder: CDN_LAYOUT_FOLDER,
    colorScheme: {
      primary: tokens.primary,
      secondary: tokens.secondary,
      primaryLight: tokens.primaryLight,
      dark: tokens.dark,
    },
    assets: {
      styles: [...THEME_STYLES],
      scripts: [],
      favicon: '/img/favicon.ico',
      logo: '/img/logo-m.png',
    },
    status: 'active',
  }
}

export const AVAILABLE_THEMES: Record<string, ThemeMetadata> = Object.fromEntries(
  Object.entries(HT_GOLD_THEMES).map(([id, tokens]) => [id, buildTheme(id, tokens)])
)

export const getActiveThemes = (): ThemeMetadata[] =>
  Object.values(AVAILABLE_THEMES).filter((t) => t.status === 'active')

export const getThemeById = (id: string): ThemeMetadata | null => AVAILABLE_THEMES[id] ?? null

export const getThemeIds = (): string[] => Object.keys(AVAILABLE_THEMES)

export const getDefaultTheme = (): ThemeMetadata =>
  AVAILABLE_THEMES[DEFAULT_HT_GOLD_THEME_ID]

export const normalizeHtGoldThemeId = (raw: string | null | undefined): string | null => {
  if (!raw) return null
  const id = raw.trim().toLowerCase()
  return AVAILABLE_THEMES[id] ? id : null
}

/** Parse `user-ht-gold/gold-navy` (or suffix only) → theme id */
export const parseHtGoldThemeFromTemplate = (raw: string | null | undefined): string | null => {
  if (!raw) return null
  const trimmed = raw.trim().toLowerCase()
  if (trimmed.startsWith('user-ht-gold/')) {
    return normalizeHtGoldThemeId(trimmed.slice('user-ht-gold/'.length))
  }
  return normalizeHtGoldThemeId(trimmed)
}
